import React from 'react'

import clsx from 'clsx'

import Card, { CardActionArea, CardContent, CardMedia } from 'components/Card'
import NextImage from 'components/NextImage'
import Tag from 'components/Tag'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import openUrl from 'helpers/openUrl'
import { useDate } from 'hooks/useDate/useDate'
import { ROUTE_NAMES } from 'routes'
import routeService from 'services/routes'

import classes from './ArticleCard.module.scss'

const getTag = (post, variant) => {
  if (variant === 'topic' && post.topic) {
    return {
      label: post.topic.name,
      slug: post.topic.slug,
      colorVariant: 'teal',
      url: routeService.generatePath(ROUTE_NAMES.RESOURCES_TOPIC, { slug: post.topic.slug }),
      engagementDestination: 'go-to_topic',
      engagementDetails: 'chip_topic-link',
    }
  }

  if (variant === 'author' && post.authors.items[0]) {
    return {
      label: post.authors.items[0].authorName,
      slug: post.authors.items[0].slug,
      colorVariant: 'purple',
      url: routeService.generatePath(ROUTE_NAMES.RESOURCES_AUTHOR, { slug: post.authors.items[0].slug }),
      engagementDestination: 'go-to_author',
      engagementDetails: 'chip_author-link',
    }
  }

  return null
}

/** @type {import("./types").default} */
const ArticleCard = ({
  className,
  isElevated = true,
  hideTags = false,
  isLazy = true,
  post,
  priority = false,
  tagVariant = 'topic',
}) => {
  const { addEvent } = useEventContext({
    engagementComponent: 'resources_article',
  })
  const { getDate } = useDate()

  const tag = getTag(post, tagVariant)

  return (
    <Card
      className={clsx('ArticleCard', className, classes.root, {
        [classes.isElevated]: isElevated,
      })}
    >
      <CardActionArea
        className={classes.linkWrap}
        onClick={() =>
          openUrl(routeService.generatePath(ROUTE_NAMES.RESOURCES_POST, { slug: post.permalink }), {
            rel: null,
            target: '_self',
          })
        }
        component="div"
        data-testid="article-card-item"
        {...addEvent({
          engagementDestination: 'go-to_article',
          engagementDetails: 'contentful_article',
          name: 'engagement_facet_click',
        })}
      >
        {post.titleImage && (
          <CardMedia
            className={clsx(
              classes.imageDiv,
              // trick to have control over the lcp element
              { [classes.lcp]: priority }
            )}
          >
            <NextImage
              src={post.titleImage.url}
              alt={`${post.slug}-image`}
              width={640}
              height={395}
              loading={isLazy ? 'lazy' : 'eager'}
              priority={priority}
            />
          </CardMedia>
        )}
        <CardContent className={classes.textContent}>
          {!hideTags && tag && (
            <Tag
              color={tag.colorVariant}
              size="small"
              href={tag.url}
              onClick={e => e.stopPropagation()}
              {...addEvent({
                engagementDestination: tag.engagementDestination,
                engagementDetails: tag.engagementDetails,
                name: 'engagement_facet_click',
              })}
            >
              {tag.label}
            </Tag>
          )}
          <Typography
            href={routeService.generatePath(ROUTE_NAMES.RESOURCES_POST, { slug: post.permalink })}
            component="a"
            variant="caption-default"
            className={clsx(classes.title, 'prevent-event')}
          >
            {post.title}
          </Typography>
          <Typography className={classes.info} variant="body-xs">
            {post.readingTime ? `${post.readingTime} min read - ` : ''}
            {getDate(post.publishDate).format('MMM DD, YYYY')}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default ArticleCard
